import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
    X,
    MapPin,
    TrendingUp,
    Ruler,
    Mountain,
    Calendar,
    AlertCircle,
    Info,
    CheckCircle,
    Copy,
    Hash,
    Download,
    RefreshCw,
    Activity,
    Maximize2,
    Home,
} from "lucide-react";
import { exportAnalysisToPDF } from "../../utils/ProfessionalPDFExport";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { detectJurisdiction, getZoningMetric, getJurisdictionInfo } from "./MapModal/utils/jurisdictionHelpers";

import Tooltip from "../UI/Tooltip";

const PropertyDetailModal = ({ isOpen, onClose, analysis }) => {
    const { canExport } = useFeatureAccess();
    const navigate = useNavigate();
    const { analysisHistory } = useSelector((state) => state.analysis);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Slope category helper
    const getSlopeCategory = (slope) => {
        if (slope == null) return { text: "Unknown", color: "gray", icon: Info };
        if (slope > 25) return { text: "Steep Terrain", color: "red", icon: AlertCircle };
        if (slope > 15) return { text: "Moderate Slope", color: "orange", icon: Info };
        return { text: "Flat", color: "green", icon: CheckCircle };
    };


    const jur = detectJurisdiction(analysis?.address || "");
    const jurInfo = getJurisdictionInfo(jur);
    const jurName = jurInfo?.name;

    // Calculate buildable percentage
    const buildablePercentage =
        analysis?.mda_ft2 && analysis?.parcel_area_acres
            ? ((analysis.mda_ft2 / (analysis.parcel_area_acres * 43560)) * 100).toFixed(1)
            : null;


    // Calculate metrics based on jurisdiction
    const slope = analysis?.avg_slope_percent_LAH || 0;
    const acres = analysis?.parcel_area_acres || 0;
    const calcResult = getZoningMetric(jur, slope, acres, analysis?.zone_code || "R1");
    const convertedMetricValue = calcResult?.convertedValue;
    const calc = calcResult;

    const slopeValue = analysis?.avg_slope_percent_LAH != null ? analysis?.avg_slope_percent_LAH : 0;
    const slopeCategory = getSlopeCategory(slopeValue);
    const SlopeIcon = slopeCategory.icon;

    // Benchmarking Logic
    const benchmarks = useMemo(() => {
        if (!analysisHistory || analysisHistory.length === 0 || !analysis) return null;
        
        const cityProperties = analysisHistory.filter(p => detectJurisdiction(p.address) === jur);
        if (cityProperties.length < 2) return null; // Need at least one other property to compare

        const avgMDA = cityProperties.reduce((sum, p) => sum + (p.mda_ft2 || 0), 0) / cityProperties.length;
        const avgMFA = cityProperties.reduce((sum, p) => sum + (p.mfa_ft2 || 0), 0) / cityProperties.length;
        
        return {
            mdaDiff: analysis.mda_ft2 ? ((analysis.mda_ft2 - avgMDA) / avgMDA * 100).toFixed(0) : null,
            mfaDiff: analysis.mfa_ft2 ? ((analysis.mfa_ft2 - avgMFA) / avgMFA * 100).toFixed(0) : null,
            cityCount: cityProperties.length
        };
    }, [analysisHistory, jur, analysis]);

    if (!isOpen || !analysis) return null;

    const handleExport = async () => {
        if (canExport) {
            try {
                toast.info("Preparing survey-standard report package...", { autoClose: 1800 });
                await exportAnalysisToPDF({
                    ...analysis,
                    jurisdictionName: jurName,
                    acres,
                    parcelFt2: analysis.parcel_area_ft2 || acres * 43560,
                    convMetric: convertedMetricValue,
                    calcResult: calc,
                });
            } catch (error) {
                console.error("Export failure:", error);
                toast.error("There was an error generating your PDF report.");
            }
        } else {
            toast.info(
                <div className="flex flex-col gap-2">
                    <span className="font-bold">Pro Feature</span>
                    <span>Upgrade to Professional plan to export PDF reports.</span>
                    <button
                        onClick={() => navigate('/#subscription-plans')}
                        className="mt-2 bg-white text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold self-start hover:bg-blue-50"
                    >
                        View Plans
                    </button>
                </div>,
                { autoClose: 5000 }
            );
        }
    };

    const handleNewAnalysis = () => {
        onClose(); // Close modal before new analysis
        // Could trigger navigation to search view here if integrated
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col animate-slideUp">
                {/* Enhanced Header with Gradient */}
                <div className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 px-8 py-8">
                    <div className="flex justify-between items-start flex-wrap gap-4">
                        <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-3">
                                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-2">
                                    <MapPin className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-blue-100 text-sm font-medium">
                                    Property Analysis Report
                                </span>
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-2 leading-tight">
                                {analysis.address || "Property Details"}
                            </h2>
                            <div className="flex items-center space-x-4 text-blue-100 text-sm flex-wrap gap-2">
                                <div className="flex items-center space-x-1">
                                    <span className="font-semibold">APN:</span>
                                    <span className="font-mono bg-white bg-opacity-20 px-2 py-1 rounded">
                                        {analysis.apn || "N/A"}
                                    </span>
                                    {analysis.apn && (
                                        <button
                                            onClick={() => copyToClipboard(analysis.apn)}
                                            className="ml-1 hover:bg-white hover:bg-opacity-20 p-1 rounded transition-colors"
                                            title="Copy APN"
                                        >
                                            <Copy className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleExport}
                                className={`p-2.5 rounded-xl transition-all backdrop-blur-sm group ${canExport
                                    ? "bg-white bg-opacity-20 hover:bg-white hover:bg-opacity-30"
                                    : "bg-white bg-opacity-10 cursor-not-allowed opacity-70"
                                    }`}
                                title={canExport ? "Export Report" : "Upgrade to Export PDF"}
                            >
                                <Download className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
                            </button>
                            <button
                                onClick={handleNewAnalysis}
                                className="p-2.5 bg-white bg-opacity-20 hover:bg-white hover:bg-opacity-30 rounded-xl transition-all backdrop-blur-sm group"
                                title="New Analysis"
                            >
                                <RefreshCw className="w-5 h-5 text-white group-hover:rotate-180 transition-transform duration-500" />
                            </button>
                            <button
                                onClick={onClose}
                                className="text-white hover:bg-white hover:bg-opacity-20 rounded-xl p-2.5 transition-all transform hover:scale-110 ml-2"
                                title="Close"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="overflow-y-auto flex-1 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="p-8 space-y-6">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-purple-600/5 to-indigo-600/5 rounded-3xl blur-3xl"></div>
                            <div className="relative bg-gradient-to-br from-white to-blue-50/30 rounded-2xl p-6 shadow-2xl border-2 border-blue-100">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center space-x-3">
                                        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl p-2.5 shadow-lg">
                                            <Activity className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-slate-900">
                                                Critical Development Metrics
                                            </h3>
                                            <p className="text-xs text-slate-600 font-medium">
                                                Key indicators for property development
                                            </p>
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg animate-pulse">
                                        PRIORITY
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {/* Average Slope (LAH) */}
                                    <div className="group relative">
                                        <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity"></div>
                                        <div className="relative bg-white rounded-xl p-4 shadow-lg border-2 border-purple-200 hover:border-purple-400 transition-all hover:shadow-xl hover:-translate-y-0.5 duration-300">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg p-2">
                                                    <Mountain className="w-5 h-5 text-purple-600" />
                                                </div>
                                                <span className="bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-1 rounded-full">
                                                    {jurName}
                                                </span>
                                            </div>
                                            <div className="mb-1">
                                                <div className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1">
                                                    Average Slope
                                                </div>
                                                <div className="text-3xl font-black text-slate-900 mb-0.5">
                                                    {analysis.avg_slope_percent_LAH != null ? analysis.avg_slope_percent_LAH : "N/A"}
                                                    <span className="text-xl text-slate-400 font-medium">%</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-1 text-xs text-slate-500 font-bold uppercase tracking-tight">
                                                <SlopeIcon className={`w-3.5 h-3.5 text-${slopeCategory.color}-600`} />
                                                <span className={`text-${slopeCategory.color}-600`}>{slopeCategory.text}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Zoning Metric (Dynamic: LUF, NSA, FAR, etc.) */}
                                    <div className="group relative">
                                        <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity"></div>
                                        <div className="relative bg-white rounded-xl p-4 shadow-lg border-2 border-blue-200 hover:border-blue-400 transition-all hover:shadow-xl hover:-translate-y-0.5 duration-300">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg p-2">
                                                    <Hash className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <span
                                                    title={calc?.formulaTooltip}
                                                    className={`text-[10px] font-bold px-2 py-1 rounded-full cursor-help ${jur === 'palo_alto' || jur === 'cupertino'
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : jur === 'los_altos_hills' || jur === 'los_altos'
                                                            ? 'bg-emerald-100 text-emerald-700'
                                                            : 'bg-slate-100 text-slate-700'
                                                        }`}>
                                                    {calc?.formulaLabel || "Metric"}
                                                </span>
                                            </div>
                                            <div className="mb-1">
                                                <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">
                                                    {calc?.formulaLabel || "Zoning Metric"}
                                                </div>
                                                <div className="text-3xl font-black text-slate-900 font-mono">
                                                    {convertedMetricValue != null ? convertedMetricValue.toFixed(2) : "N/A"}
                                                    {convertedMetricValue != null && <span className="text-sm font-bold text-slate-500 ml-1">ac</span>}
                                                </div>
                                            </div>
                                            <div className="text-[10px] text-slate-500 font-semibold leading-normal break-words">
                                                {jurName === 'Los Gatos' ? (
                                                    <Tooltip content={calc?.formulaTooltip}>
                                                        <span className="cursor-help hover:text-blue-600 transition-colors flex items-center gap-1">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                                            Combined FAR (Hover for details)
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <div title={calc?.formulaTooltip}>
                                                        {calc?.formulaTooltip || "Calculated development coefficient"}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Maximum Development Area (MDA) */}
                                    <div className="group relative">
                                        <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity"></div>
                                        <div className="relative bg-white rounded-xl p-4 shadow-lg border-2 border-green-200 hover:border-green-400 transition-all hover:shadow-xl hover:-translate-y-0.5 duration-300">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-lg p-2">
                                                    <Maximize2 className="w-5 h-5 text-green-600" />
                                                </div>
                                                <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
                                                    {jurInfo?.mdaLabel || analysis.mda_label || "MDA"}
                                                </span>
                                            </div>
                                            <div className="mb-1">
                                                <div className="text-xs font-bold text-green-600 uppercase tracking-wider mb-1">
                                                    {jurInfo?.mdaLabel || analysis.mda_label || "Max Development Area"}
                                                </div>
                                                <div className="text-2xl font-black text-slate-900 mb-0.5">
                                                    {analysis.mda_ft2 || "N/A"}
                                                    <span className="text-sm text-slate-400 ml-0.5 font-bold uppercase">ft²</span>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">
                                                    {analysis.mda_ft2
                                                        ? `${(analysis.mda_ft2 / 43560).toFixed(2)} acres`
                                                        : "Not calculated"}
                                                </div>
                                                {benchmarks?.mdaDiff && (
                                                    <div className={`text-[10px] font-black px-1.5 py-0.5 rounded inline-block w-fit ${parseInt(benchmarks.mdaDiff) >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                                                        {parseInt(benchmarks.mdaDiff) >= 0 ? '+' : ''}{benchmarks.mdaDiff}% vs City Avg
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
 
                                    {/* Maximum Floor Area (MFA) */}
                                    <div className="group relative">
                                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity"></div>
                                        <div className="relative bg-white rounded-xl p-4 shadow-lg border-2 border-indigo-200 hover:border-indigo-400 transition-all hover:shadow-xl hover:-translate-y-0.5 duration-300">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-lg p-2">
                                                    <Home className="w-5 h-5 text-indigo-600" />
                                                </div>
                                                <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-1 rounded-full">
                                                    {jurInfo?.mfaLabel || analysis.mfa_label || "MFA"}
                                                </span>
                                            </div>
                                            <div className="mb-1">
                                                <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1">
                                                    {jurInfo?.mfaLabel || analysis.mfa_label || "Max Floor Area"}
                                                </div>
                                                <div className="text-2xl font-black text-slate-900 mb-0.5">
                                                    {analysis.mfa_ft2 || "N/A"}
                                                    <span className="text-sm text-slate-400 ml-0.5 font-bold uppercase">ft²</span>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">
                                                    {analysis.mfa_ft2
                                                        ? `${(analysis.mfa_ft2 / 43560).toFixed(2)} acres`
                                                        : "Not calculated"}
                                                </div>
                                                {benchmarks?.mfaDiff && (
                                                    <div className={`text-[10px] font-black px-1.5 py-0.5 rounded inline-block w-fit ${parseInt(benchmarks.mfaDiff) >= 0 ? 'bg-indigo-50 text-indigo-700' : 'bg-rose-50 text-rose-700'}`}>
                                                        {parseInt(benchmarks.mfaDiff) >= 0 ? '+' : ''}{benchmarks.mfaDiff}% vs City Avg
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Reference Bar */}
                                <div className="mt-4 bg-gradient-to-r from-slate-100 to-slate-50 rounded-xl p-3 border border-slate-200">
                                    <div className="flex items-center justify-between flex-wrap gap-3 text-sm">
                                        <div className="flex items-center space-x-2">
                                            <Info className="w-4 h-4 text-slate-600" />
                                            <span className="font-bold text-slate-700">
                                                Development Potential:
                                            </span>
                                            <span className="font-black text-blue-600">
                                                {buildablePercentage ? `${buildablePercentage}%` : "N/A"}
                                            </span>
                                            <span className="text-slate-600">of parcel</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <span className="text-slate-600">Total:</span>
                                            <span className="font-black text-slate-900">
                                                {analysis.parcel_area_acres
                                                    ? `${analysis.parcel_area_acres} ac`
                                                    : "N/A"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Jurisdiction Specific Alert Banners (CDP, Slope Penalty, etc.) */}
                        {(analysis.requires_cdp || (jur === 'fremont' && slopeValue > 30)) && (
                            <div className={`bg-gradient-to-r ${analysis.requires_cdp ? 'from-amber-50 to-orange-50 border-amber-200' : 'from-red-50 to-rose-50 border-red-200'} border-2 rounded-2xl p-5 flex items-start space-x-4 shadow-md`}>
                                <div className={`${analysis.requires_cdp ? 'bg-amber-100' : 'bg-red-100'} rounded-full p-3 flex-shrink-0`}>
                                    <AlertCircle className={`w-6 h-6 ${analysis.requires_cdp ? 'text-amber-600' : 'text-red-600'}`} />
                                </div>
                                <div className="flex-1">
                                    <h4 className={`font-bold ${analysis.requires_cdp ? 'text-amber-900' : 'text-red-900'} mb-2 text-lg`}>
                                        {analysis.requires_cdp ? 'Coastal Development Permit Required' : 'Development Restricted'}
                                    </h4>
                                    <p className={`${analysis.requires_cdp ? 'text-amber-800' : 'text-red-800'} text-sm leading-relaxed`}>
                                        {analysis.requires_cdp
                                            ? `This property requires a Coastal Development Permit (CDP) due to its location and development characteristics.`
                                            : `This property is in a high-slope area (>30%) where development is strictly restricted by ${jurName} municipal code.`}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Quick Insights */}
                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                            <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center space-x-2">
                                <TrendingUp className="w-6 h-6 text-blue-600" />
                                <span>Quick Insights</span>
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <SlopeIcon
                                            className={`w-5 h-5 text-${slopeCategory.color}-600`}
                                        />
                                        <span className="text-xs font-bold text-slate-600 uppercase">
                                            Terrain Type
                                        </span>
                                    </div>
                                    <div
                                        className={`text-lg font-bold text-${slopeCategory.color}-600`}
                                    >
                                        {slopeCategory.text}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        {analysis.avg_slope_percent_LAH != null
                                            ? `${analysis.avg_slope_percent_LAH}% average grade`
                                            : "No data"}
                                    </div>
                                </div>

                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <Info className="w-5 h-5 text-blue-600" />
                                        <span className="text-xs font-bold text-slate-600 uppercase">
                                            Buildable Area
                                        </span>
                                    </div>
                                    <div className="text-lg font-bold text-blue-600">
                                        {buildablePercentage ? `${buildablePercentage}%` : "N/A"}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        of total parcel area
                                    </div>
                                </div>

                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <Mountain className="w-5 h-5 text-purple-600" />
                                        <span className="text-xs font-bold text-slate-600 uppercase">
                                            Complexity
                                        </span>
                                    </div>
                                    <div className="text-lg font-bold text-purple-600">
                                        {analysis.contour_count > 50
                                            ? "High"
                                            : analysis.contour_count > 25
                                                ? "Medium"
                                                : "Low"}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        {analysis.contour_count
                                            ? `${analysis.contour_count} contour lines`
                                            : "No data"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Detailed Information Sections */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Topography Details */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                                <div className="flex items-center space-x-3 mb-5">
                                    <div className="bg-purple-100 rounded-xl p-3">
                                        <Mountain className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900">
                                        Topography Analysis
                                    </h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center py-3 border-b border-slate-100 bg-slate-50 px-3 rounded-xl">
                                        <span className="text-sm text-slate-600 font-bold uppercase tracking-tight">
                                            Average Slope ({jur === 'los_altos_hills' ? 'LAH' : jurName})
                                        </span>
                                        <span className="text-sm font-black text-slate-900 font-mono">
                                            {analysis.avg_slope_percent_LAH != null
                                                ? `${analysis.avg_slope_percent_LAH}%`
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                                        <span className="text-sm text-slate-600 font-medium">
                                            Elevation
                                        </span>
                                        <span className="text-sm font-bold text-slate-900">
                                            {analysis.elevation_field || "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                                        <span className="text-sm text-slate-600 font-medium">
                                            Contour Lines
                                        </span>
                                        <span className="text-sm font-bold text-slate-900">
                                            {analysis.contour_count || "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                                        <span className="text-sm text-slate-600 font-medium">
                                            Total Contour Length
                                        </span>
                                        <span className="text-sm font-bold text-slate-900">
                                            {analysis.contour_total_length_ft
                                                ? `${analysis.contour_total_length_ft} ft`
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-3 border-b border-slate-100 bg-slate-50 px-3 rounded-xl">
                                        <span className="text-sm text-slate-600 font-bold uppercase tracking-tight">
                                            {calc?.formulaLabel || "Zoning Metric"}
                                        </span>
                                        <span className="text-sm font-black text-slate-900 font-mono">
                                            {convertedMetricValue != null ? `${convertedMetricValue.toFixed(2)} ac` : "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-3">
                                        <span className="text-sm text-slate-600 font-medium">
                                            Parcel Area (ft²)
                                        </span>
                                        <span className="text-sm font-bold text-slate-900 font-mono">
                                            {analysis.parcel_area_ft2 || "N/A"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Development Metrics */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                                <div className="flex items-center space-x-3 mb-5">
                                    <div className="bg-green-100 rounded-xl p-3">
                                        <Ruler className="w-6 h-6 text-green-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900">
                                        Development Metrics
                                    </h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center py-4 border-b border-slate-100 bg-slate-50 px-3 rounded-xl mb-2">
                                        <span className="text-sm text-slate-600 font-bold uppercase tracking-tight">
                                            {jurInfo?.mdaLabel || analysis.mda_label || "Maximum Development Area (MDA)"}
                                        </span>
                                        <span className="text-sm font-black text-slate-900 font-mono">
                                            {analysis.mda_ft2
                                                ? `${analysis.mda_ft2.toLocaleString()} ft²`
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-4 border-b border-slate-100 bg-slate-50 px-3 rounded-xl mb-2">
                                        <span className="text-sm text-slate-600 font-bold uppercase tracking-tight">
                                            {jurInfo?.mfaLabel || analysis.mfa_label || "Max Floor Area (MFA)"}
                                        </span>
                                        <span className="text-sm font-black text-slate-900 font-mono">
                                            {analysis.mfa_ft2
                                                ? `${analysis.mfa_ft2.toLocaleString()} ft²`
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                                        <span className="text-sm text-slate-600 font-medium">
                                            MDA in Acres
                                        </span>
                                        <span className="text-sm font-bold text-slate-900">
                                            {analysis.mda_ft2
                                                ? `${(analysis.mda_ft2 / 43560).toFixed(4)} ac`
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-3">
                                        <span className="text-sm text-slate-600 font-medium">
                                            MFA in Acres
                                        </span>
                                        <span className="text-sm font-bold text-slate-900">
                                            {analysis.mfa_ft2
                                                ? `${(analysis.mfa_ft2 / 43560).toFixed(4)} ac`
                                                : "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-3">
                                        <span className="text-sm text-slate-600 font-medium">
                                            Parcel Area (acres)
                                        </span>
                                        <span className="text-sm font-bold text-slate-900 font-mono">
                                            {analysis.parcel_area_acres || "N/A"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Enhanced Footer */}
                <div className="bg-white border-t border-slate-200 px-8 py-5">
                    <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                        <div className="flex items-center space-x-2 text-sm text-slate-500">
                            <Calendar className="w-4 h-4" />
                            <span>Report generated on</span>
                        </div>
                        <div className="flex space-x-3 flex-wrap justify-center">
                                <button
                                onClick={handleNewAnalysis}
                                className="px-5 py-2.5 bg-slate-900 text-white rounded-xl hover:shadow-lg transition-all font-bold flex items-center space-x-2 border-b-2 border-slate-700 active:translate-y-0.5 active:border-b-0"
                            >
                                <RefreshCw className="w-4 h-4" />
                                <span>New Analysis</span>
                            </button>
                            <button
                                onClick={() => window.open('mailto:architect@privateopenhouse.com?subject=Inquiry%20for%20' + analysis.address)}
                                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg transition-all font-bold flex items-center space-x-2"
                            >
                                <CheckCircle className="w-4 h-4" />
                                <span>Verify with Partner Architect</span>
                            </button>
                            <button
                                onClick={handleExport}
                                className={`px-5 py-2.5 rounded-xl transition-all font-semibold border border-slate-200 flex items-center space-x-2 ${canExport
                                    ? "text-slate-600 hover:bg-slate-100"
                                    : "text-slate-400 bg-slate-50 cursor-not-allowed"
                                    }`}
                                title={canExport ? "Export PDF" : "Upgrade to Export PDF"}
                            >
                                <Download className="w-4 h-4" />
                                <span>Export PDF</span>
                            </button>
                            <button
                                onClick={onClose}
                                className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition-all font-semibold border border-slate-200 flex items-center space-x-2 ml-2"
                            >
                                <X className="w-4 h-4" />
                                <span>Close</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyDetailModal;
